import { useEffect } from "react";
import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Box,
  Button,
  CloseButton,
  Flex,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  HStack,
  Heading,
  IconButton,
  Image,
  Input,
  Select,
  Spacer,
  Text,
  Textarea,
  VStack,
  Wrap,
  WrapItem,
  useDisclosure,
} from "@chakra-ui/react";
import {
  BiBuildings,
  BiCalendarAlt,
  BiCamera,
  BiCar,
  BiMap,
  BiPhone,
  BiTrash,
  BiUser,
} from "react-icons/bi";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { Link, useLocation, useNavigate } from "react-router-dom";

import LocationPickerEmployee from "components/shared/locationPicker_employee";
import { useLangContext } from "configs/languageContext";
import { form, form_fields } from "data/translations";
import { accident } from "data/data";
import { TemplateForm } from "components/layout/template";
import { useDataContext } from "configs/dataContext";
import processFormData from "./processFormData";

const C = {
  primary: "#059669",
  primaryDark: "#047857",
  primaryLight: "#ECFDF5",
  primarySoft: "#D1FAE5",
  emergency: "#DC2626",
  emergencySoft: "#FEE2E2",
  ink: "#0F172A",
  muted: "#64748B",
  border: "#E2E8F0",
  bg: "#F8FAFC",
};

const inputProps = {
  size: "lg" as const,
  bg: "white",
  borderColor: C.border,
  focusBorderColor: C.primary,
  borderRadius: "lg" as const,
  fontSize: "md" as const,
};

const labelProps = {
  fontWeight: "600" as const,
  color: C.ink,
  fontSize: "sm" as const,
  mb: 1.5,
};

const Section = ({
  number, title, helper, icon, children,
}: {
  number: number; title: string; helper?: string;
  icon?: React.ReactNode; children: React.ReactNode;
}) => (
  <Box>
    <HStack mb={4} spacing={3} align="center">
      <Flex
        w="32px" h="32px"
        align="center" justify="center"
        bg={C.primaryLight} color={C.primary}
        borderRadius="full"
        fontWeight="800" fontSize="sm"
      >
        {number}
      </Flex>
      <Box flex="1">
        <HStack spacing={2} align="center">
          {icon}
          <Heading as="h2" size="md" fontWeight="700" color={C.ink} letterSpacing="-0.01em">
            {title}
          </Heading>
        </HStack>
        {helper && <Text fontSize="xs" color={C.muted} mt={0.5}>{helper}</Text>}
      </Box>
    </HStack>
    <VStack spacing={4} align="stretch">{children}</VStack>
  </Box>
);

export default function EmployeeFormPage() {
  const { lang } = useLangContext();
  const { addData, editData } = useDataContext();
  const navigate = useNavigate();
  const location = useLocation();
  const t = (key: string) => form[key]?.[lang] ?? key;

  const {
    isOpen: isVisibleFail,
    onClose: onCloseFail,
    onOpen: onOpenFail,
  } = useDisclosure({ defaultIsOpen: false });
  const {
    isOpen: isVisibleSuccess,
    onClose: onCloseSuccess,
    onOpen: onOpenSuccess,
  } = useDisclosure({ defaultIsOpen: false });

  const {
    register, control, handleSubmit, watch,
    formState: { errors, isSubmitting },
    setValue, reset, getValues,
  } = useForm<accident>();

  const isEditing = Boolean(location.state?.id);

  const onSubmit: SubmitHandler<accident> = async (data: accident) => {
    onCloseFail();
    delete data.picture_curr;

    try {
      if (!isEditing) {
        const processedData = processFormData(data, "employee");
        await addData(processedData);
        reset();
        onOpenSuccess();
      } else {
        await editData(data);
        onOpenSuccess();
        navigate("/employee/data");
      }
    } catch (e) {
      console.error("Submit failed:", e);
      onOpenFail();
    }
  };

  useEffect(() => {
    if (location.state !== null) {
      const s = location.state;
      const fields: (keyof accident)[] = [
        "id", "submission_type", "validation_status", "source",
        "person_reporting", "person_reporting_email", "person_reporting_phone", "person_attending_accident",
        "accident_location", "accident_type", "accident_event_details", "accident_required_measures",
        "picture_links", "animal_condition", "animal_species", "animal_sex", "animal_age",
        "transportation_performed", "transportation_destination", "transportation_destination_details",
        "accident_site_details", "electrocution_pole_number", "road_kilometer_number",
        "accident_site_details_locality", "accident_site_details_additional",
        "SITADA", "electricity_management_number", "salvemonos_letter_number", "comments",
      ];
      fields.forEach((f) => setValue(f as any, s[f as string]));
      if (s.accident_timestamp?.seconds) {
        setValue("accident_timestamp", new Date(s.accident_timestamp.seconds * 1000));
      }
    }
  }, [location, setValue]);

  const handleImageUpload = () => {
    const img = getValues("picture_curr");
    if (img && img.length > 0) {
      const files: any = getValues("picture_links") || [];
      files.push(img[0]);
      setValue("picture_links", files);
    }
  };
  const handleImageDelete = (ind: number) => {
    const cur = getValues("picture_links");
    setValue("picture_links", [...cur.slice(0, ind), ...cur.slice(ind + 1)]);
  };

  const picCount = Array.isArray(watch("picture_links")) ? watch("picture_links").length : 0;
  const transportYes = watch("transportation_performed")?.toString() === "yes";

  return (
    <TemplateForm>
      {/* Header */}
      <HStack mb={4} justify="space-between">
        <Box>
          <Heading as="h1" size="lg" color={C.ink} letterSpacing="-0.015em">
            {isEditing
              ? (lang === "es" ? "Editar reporte" : lang === "en" ? "Edit report" : "Modifier")
              : t("head")}
          </Heading>
          {isEditing && (
            <Text fontSize="xs" color={C.muted} mt={0.5}>
              ID: {location.state.id}
            </Text>
          )}
        </Box>
        <Button as={Link} to="/employee/data" variant="ghost" size="sm" color={C.muted}>
          ← {t("go_back")}
        </Button>
      </HStack>

      {isVisibleSuccess && (
        <Alert
          status="success"
          mb={6}
          borderRadius="xl"
          borderWidth="1px"
          borderColor={C.primarySoft}
          bg={C.primaryLight}
        >
          <AlertIcon color={C.primary} />
          <Box flex="1">
            <AlertTitle color={C.ink}>{t("popup_success_title")}</AlertTitle>
            <AlertDescription color={C.muted} fontSize="sm">{t("popup_success_text")}</AlertDescription>
          </Box>
          <CloseButton onClick={onCloseSuccess} />
        </Alert>
      )}

      <form onSubmit={handleSubmit(onSubmit)}>
        <VStack spacing={10} align="stretch">

          {/* 1 — Contact */}
          <Section number={1} title={t("personal")} icon={<BiUser color={C.muted} />}>
            <FormControl>
              <FormLabel {...labelProps}>{t("person_reporting")}</FormLabel>
              <Input {...inputProps} type="text" {...register("person_reporting", {})} />
            </FormControl>

            <HStack spacing={3} align="flex-start" flexWrap={{ base: "wrap", md: "nowrap" }}>
              <FormControl flex="1" minW={{ base: "100%", md: "auto" }}>
                <FormLabel {...labelProps}>{t("person_reporting_email")}</FormLabel>
                <Input {...inputProps} type="email" {...register("person_reporting_email", {})} />
              </FormControl>
              <FormControl flex="1" minW={{ base: "100%", md: "auto" }}>
                <FormLabel {...labelProps}>{t("person_reporting_phone")}</FormLabel>
                <Input {...inputProps} type="tel" {...register("person_reporting_phone", {})} />
              </FormControl>
            </HStack>

            <FormControl>
              <FormLabel {...labelProps}>{t("person_attending_accident")}</FormLabel>
              <Input {...inputProps} {...register("person_attending_accident", {})} />
            </FormControl>
          </Section>

          {/* 2 — Accident details */}
          <Section number={2} title={t("accident")} icon={<BiCalendarAlt color={C.muted} />}>
            <FormControl>
              <FormLabel {...labelProps}>{t("accident_timestamp")}</FormLabel>
              <Controller
                control={control}
                name="accident_timestamp"
                defaultValue={new Date()}
                render={({ field }) => {
                  const d = field.value instanceof Date
                    ? field.value
                    : field.value ? new Date(field.value as any) : new Date();
                  const iso = isNaN(d.getTime()) ? new Date().toISOString().slice(0, 10) : d.toISOString().slice(0, 10);
                  const today = new Date().toISOString().slice(0, 10);
                  return (
                    <Input
                      {...inputProps}
                      type="date"
                      value={iso}
                      max={today}
                      onChange={(e) => field.onChange(e.target.value ? new Date(e.target.value) : new Date())}
                    />
                  );
                }}
              />
            </FormControl>

            <FormControl>
              <FormLabel {...labelProps}>{t("accident_type")}</FormLabel>
              <Select {...inputProps} {...register("accident_type", {})}>
                {Object.keys(form_fields.accident_type).map((k: any) => (
                  <option key={k} value={k}>
                    {form_fields.accident_type[k][lang] ?? form_fields.accident_type[k].es}
                  </option>
                ))}
              </Select>
            </FormControl>

            <FormControl>
              <FormLabel {...labelProps}>{t("accident_event_details")}</FormLabel>
              <Textarea {...inputProps} rows={3} {...register("accident_event_details", {})} />
            </FormControl>

            <FormControl>
              <FormLabel {...labelProps}>{t("accident_required_measures")}</FormLabel>
              <Select {...inputProps} {...register("accident_required_measures", {})}>
                {Object.keys(form_fields.accident_required_measures).map((k: any) => (
                  <option key={k} value={k}>
                    {form_fields.accident_required_measures[k][lang] ?? form_fields.accident_required_measures[k].es}
                  </option>
                ))}
              </Select>
            </FormControl>
          </Section>

          {/* 3 — Location */}
          <Section number={3} title={t("accident_location")} icon={<BiMap color={C.muted} />}>
            <FormControl isInvalid={Boolean(errors.accident_location)}>
              <FormHelperText fontSize="xs" color={C.muted} mt={-1} mb={2}>
                {t("accident_location_help")}
              </FormHelperText>
              <Box
                borderRadius="lg"
                borderWidth={errors.accident_location ? "2px" : "1px"}
                borderColor={errors.accident_location ? C.emergency : C.border}
                overflow="hidden"
              >
                <Controller
                  name="accident_location"
                  control={control}
                  render={({ field }) => <LocationPickerEmployee field={field} />}
                />
              </Box>
              <FormErrorMessage fontSize="xs">{t("field_error")}</FormErrorMessage>
            </FormControl>

            <FormControl>
              <FormLabel {...labelProps}>{t("accident_site_details_locality")}</FormLabel>
              <FormHelperText fontSize="xs" color={C.muted} mt={-1} mb={2}>
                {t("accident_site_details_locality_help")}
              </FormHelperText>
              <Input {...inputProps} {...register("accident_site_details_locality", {})} />
            </FormControl>

            <FormControl>
              <FormLabel {...labelProps}>{t("accident_site_details_additional")}</FormLabel>
              <Input {...inputProps} {...register("accident_site_details_additional", {})} />
            </FormControl>
          </Section>

          {/* 4 — Photos */}
          <Section number={4} title={t("picture_links")} icon={<BiCamera color={C.muted} />}>
            <Box
              borderWidth="2px"
              borderStyle="dashed"
              borderColor={C.border}
              borderRadius="xl"
              bg={C.bg}
              p={6}
              textAlign="center"
              _hover={{ borderColor: C.primary, bg: C.primaryLight }}
              transition="all .15s ease"
              position="relative"
              cursor="pointer"
            >
              <Input
                type="file"
                accept="image/*;capture=camera"
                multiple={false}
                opacity={0}
                position="absolute"
                inset={0}
                cursor="pointer"
                {...register("picture_curr", { onChange: handleImageUpload })}
                aria-label={t("add_photo")}
              />
              <VStack spacing={2} pointerEvents="none">
                <Flex w="44px" h="44px" align="center" justify="center" bg="white" borderRadius="full" boxShadow="sm" color={C.primary}>
                  <BiCamera size="24" />
                </Flex>
                <Text fontSize="sm" fontWeight="700" color={C.ink}>{t("add_photo")}</Text>
                <Text fontSize="xs" color={C.muted}>
                  {picCount > 0 ? `${picCount} ${t("length")}` : t("picture_links_help")}
                </Text>
              </VStack>
            </Box>

            {picCount > 0 && (
              <Wrap spacing={3} mt={1}>
                {watch("picture_links").map((file: any, ind: number) => (
                  <WrapItem key={"image_" + ind}>
                    <Box position="relative" borderRadius="lg" overflow="hidden" borderWidth="1px" borderColor={C.border}>
                      <Image
                        h="120px" w="120px"
                        objectFit="cover"
                        src={typeof file === "string" ? file : URL.createObjectURL(file)}
                        alt=""
                      />
                      <IconButton
                        onClick={() => handleImageDelete(ind)}
                        icon={<BiTrash />}
                        size="xs"
                        position="absolute"
                        top={1.5}
                        right={1.5}
                        bg="white"
                        color={C.emergency}
                        _hover={{ bg: C.emergencySoft }}
                        aria-label={t("delete_photo")}
                        borderRadius="full"
                        boxShadow="sm"
                      />
                    </Box>
                  </WrapItem>
                ))}
              </Wrap>
            )}
          </Section>

          {/* 5 — Animal */}
          <Section number={5} title={t("animal")}>
            <HStack spacing={3} align="flex-start" flexWrap={{ base: "wrap", md: "nowrap" }}>
              <FormControl flex="1" minW={{ base: "100%", md: "auto" }}>
                <FormLabel {...labelProps}>{t("animal_condition")}</FormLabel>
                <Select {...inputProps} {...register("animal_condition", {})}>
                  {Object.keys(form_fields.animal_condition).map((k: any) => (
                    <option key={k} value={k}>
                      {form_fields.animal_condition[k][lang] ?? form_fields.animal_condition[k].es}
                    </option>
                  ))}
                </Select>
              </FormControl>
              <FormControl flex="1" minW={{ base: "100%", md: "auto" }}>
                <FormLabel {...labelProps}>{t("animal_species")}</FormLabel>
                <Select {...inputProps} {...register("animal_species", {})}>
                  {Object.keys(form_fields.animal_species).map((k: any) => (
                    <option key={k} value={k}>
                      {form_fields.animal_species[k][lang] ?? form_fields.animal_species[k].es}
                    </option>
                  ))}
                </Select>
              </FormControl>
            </HStack>

            <HStack spacing={3} align="flex-start" flexWrap={{ base: "wrap", md: "nowrap" }}>
              <FormControl flex="1" minW={{ base: "100%", md: "auto" }}>
                <FormLabel {...labelProps}>{t("animal_sex")}</FormLabel>
                <Select {...inputProps} {...register("animal_sex", {})}>
                  {Object.keys(form_fields.animal_sex).map((k: any) => (
                    <option key={k} value={k}>
                      {form_fields.animal_sex[k][lang] ?? form_fields.animal_sex[k].es}
                    </option>
                  ))}
                </Select>
              </FormControl>
              <FormControl flex="1" minW={{ base: "100%", md: "auto" }}>
                <FormLabel {...labelProps}>{t("animal_age")}</FormLabel>
                <Select {...inputProps} {...register("animal_age", {})}>
                  {Object.keys(form_fields.animal_age).map((k: any) => (
                    <option key={k} value={k}>
                      {form_fields.animal_age[k][lang] ?? form_fields.animal_age[k].es}
                    </option>
                  ))}
                </Select>
              </FormControl>
            </HStack>
          </Section>

          {/* 6 — Transport */}
          <Section number={6} title={t("transport")} icon={<BiCar color={C.muted} />}>
            <FormControl>
              <FormLabel {...labelProps}>{t("transportation_performed")}</FormLabel>
              <Select {...inputProps} {...register("transportation_performed", {})}>
                {Object.keys(form_fields.transportation_performed).map((k: any) => (
                  <option key={k} value={k}>
                    {form_fields.transportation_performed[k][lang] ?? form_fields.transportation_performed[k].es}
                  </option>
                ))}
              </Select>
            </FormControl>

            {transportYes && (
              <>
                <FormControl>
                  <FormLabel {...labelProps}>{t("transportation_destination")}</FormLabel>
                  <Select {...inputProps} {...register("transportation_destination", {})}>
                    {Object.keys(form_fields.transportation_destination).map((k: any) => (
                      <option key={k} value={k}>
                        {form_fields.transportation_destination[k][lang] ?? form_fields.transportation_destination[k].es}
                      </option>
                    ))}
                  </Select>
                </FormControl>
                <FormControl>
                  <FormLabel {...labelProps}>{t("transportation_destination_details")}</FormLabel>
                  <Input {...inputProps} {...register("transportation_destination_details", {})} />
                </FormControl>
              </>
            )}
          </Section>

          {/* 7 — Site / area context */}
          <Section number={7} title={t("area")} icon={<BiBuildings color={C.muted} />}>
            <FormControl>
              <FormLabel {...labelProps}>{t("accident_site_details")}</FormLabel>
              <Select {...inputProps} {...register("accident_site_details", {})}>
                {Object.keys(form_fields.accident_site_details).map((k: any) => (
                  <option key={k} value={k}>
                    {form_fields.accident_site_details[k][lang] ?? form_fields.accident_site_details[k].es}
                  </option>
                ))}
              </Select>
            </FormControl>

            <HStack spacing={3} align="flex-start" flexWrap={{ base: "wrap", md: "nowrap" }}>
              <FormControl flex="1" minW={{ base: "100%", md: "auto" }}>
                <FormLabel {...labelProps}>{t("electrocution_pole_number")}</FormLabel>
                <Input {...inputProps} {...register("electrocution_pole_number", {})} />
              </FormControl>
              <FormControl flex="1" minW={{ base: "100%", md: "auto" }}>
                <FormLabel {...labelProps}>{t("road_kilometer_number")}</FormLabel>
                <Input {...inputProps} {...register("road_kilometer_number", {})} />
              </FormControl>
            </HStack>
          </Section>

          {/* 8 — Bureaucratic + notes */}
          <Section number={8} title={t("additional")}>
            <HStack spacing={3} align="flex-start" flexWrap={{ base: "wrap", md: "nowrap" }}>
              <FormControl flex="1" minW={{ base: "100%", md: "auto" }}>
                <FormLabel {...labelProps}>{t("SITADA")}</FormLabel>
                <Input {...inputProps} {...register("SITADA", {})} />
              </FormControl>
              <FormControl flex="1" minW={{ base: "100%", md: "auto" }}>
                <FormLabel {...labelProps}>{t("electricity_management_number")}</FormLabel>
                <Input {...inputProps} {...register("electricity_management_number", {})} />
              </FormControl>
            </HStack>

            <FormControl>
              <FormLabel {...labelProps}>{t("salvemonos_letter_number")}</FormLabel>
              <Input {...inputProps} {...register("salvemonos_letter_number", {})} />
            </FormControl>

            <FormControl>
              <FormLabel {...labelProps}>{t("comments")}</FormLabel>
              <Textarea {...inputProps} rows={3} {...register("comments", {})} />
            </FormControl>
          </Section>

          {/* Submit */}
          <VStack spacing={3} pt={2}>
            <Button
              type="submit"
              w="100%"
              h={{ base: "62px", md: "72px" }}
              fontSize={{ base: "lg", md: "xl" }}
              fontWeight="800"
              letterSpacing="-0.01em"
              bg={C.primary}
              color="white"
              _hover={{ bg: C.primaryDark, transform: "translateY(-1px)", boxShadow: "xl" }}
              _active={{ transform: "translateY(0)" }}
              borderRadius="xl"
              boxShadow={`0 12px 28px ${C.primarySoft}`}
              isLoading={isSubmitting}
              loadingText={t("submitting")}
              onClick={() => onOpenFail()}
            >
              {isEditing
                ? (lang === "es" ? "Guardar cambios" : lang === "en" ? "Save changes" : "Enregistrer")
                : t("submit")}
            </Button>
            <Button as={Link} to="/employee/data" variant="ghost" fontSize="sm" color={C.muted} _hover={{ color: C.primary, bg: "transparent" }}>
              ← {t("go_back")}
            </Button>
          </VStack>
        </VStack>
      </form>

      {/* Failure toast */}
      <Alert
        status="error"
        position="fixed"
        bottom={4}
        left="50%"
        transform="translateX(-50%)"
        maxW="600px"
        borderRadius="xl"
        zIndex={10000}
        boxShadow="2xl"
        display={isVisibleFail && Object.keys(errors).length > 0 ? "flex" : "none"}
      >
        <AlertIcon />
        <Box flex="1">
          <AlertTitle fontSize="sm">{t("popup_error_title")}</AlertTitle>
          <AlertDescription fontSize="xs">{t("popup_error_text")}</AlertDescription>
        </Box>
        <Spacer />
        <CloseButton onClick={onCloseFail} />
      </Alert>
    </TemplateForm>
  );
}
